/**
 * Input component
 * @package @app/ui
 */

import * as React from 'react';
import { cn } from '../lib/utils';

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, type, onWheel, ...props }, ref) => {
    // On <input type="number">, the mouse scroll wheel SILENTLY mutates
    // the value when the input has focus. A user enters "100" → scrolls
    // past it → the value becomes 99.97 (three step="0.01" decrements)
    // with no visual cue. Blur on wheel disables that mutation while
    // still letting the user scroll the page. Caller-supplied onWheel
    // handlers run after our blur so this is purely additive.
    const handleWheel = React.useCallback(
      (e: React.WheelEvent<HTMLInputElement>) => {
        if (type === 'number') {
          e.currentTarget.blur();
        }
        onWheel?.(e);
      },
      [type, onWheel],
    );
    return (
      <input
        type={type}
        onWheel={handleWheel}
        className={cn(
          'flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-base shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 md:text-sm',
          className,
        )}
        ref={ref}
        {...props}
      />
    );
  },
);
Input.displayName = 'Input';

export { Input };
